import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Lock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SiteContext = createContext({ content: {} });
export const useSiteContent = () => useContext(SiteContext).content;

const ComingSoon = ({ content, onUnlock }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/site/unlock`, { password });
      localStorage.setItem("cw_site_token", data.site_token);
      onUnlock();
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Incorrect password");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden" style={{ background: "#13353F" }} data-testid="coming-soon-page">
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full opacity-20" style={{ background: "radial-gradient(circle, #C9A84C, transparent 70%)" }} />
      <div className="absolute -bottom-48 -left-32 w-[480px] h-[480px] rounded-full opacity-15" style={{ background: "radial-gradient(circle, #7BAE7F, transparent 70%)" }} />
      <div className="relative max-w-xl w-full mf-fade-in">
        <img src="/icons/icon-192.png" alt="Clinician Well logo" className="w-14 h-14 rounded-2xl mb-8" />
        <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: "#C9A84C" }}>Coming soon</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-white mb-5" data-testid="coming-soon-title">{content.coming_soon_title}</h1>
        <p className="text-base mb-10 max-w-md" style={{ color: "rgba(250,250,248,0.72)" }} data-testid="coming-soon-message">{content.coming_soon_message}</p>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-md">
          <div className="flex-1 flex items-center gap-2 px-4 rounded-full" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)" }}>
            <Lock className="w-4 h-4" style={{ color: "#C9A84C" }} />
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Site password"
              className="flex-1 bg-transparent py-3 outline-none text-white placeholder:text-white/40" data-testid="site-password-input" />
          </div>
          <button type="submit" disabled={busy} className="px-6 py-3 rounded-full font-medium transition-transform hover:-translate-y-0.5" style={{ background: "#C9A84C", color: "#13353F" }} data-testid="site-unlock-btn">
            {busy ? "Checking…" : "Enter"}
          </button>
        </form>
        {error && <div className="mt-3 text-sm" style={{ color: "#F2B8B5" }} data-testid="site-password-error">{error}</div>}
        <div className="mt-16 text-xs" style={{ color: "rgba(250,250,248,0.4)" }}>© {new Date().getFullYear()} Clinician Well</div>
      </div>
    </div>
  );
};

const visitorId = () => {
  let v = localStorage.getItem("cw_vid");
  if (!v) { v = crypto.randomUUID(); localStorage.setItem("cw_vid", v); }
  return v;
};

const PageTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!navigator.onLine) return;
    axios.post(`${API}/analytics/pageview`, { path: pathname, visitor_id: visitorId(), referrer: document.referrer }).catch(() => {});
  }, [pathname]);
  return null;
};

export const SiteGate = ({ children }) => {
  const [state, setState] = useState(null);
  const load = () => {
    const token = localStorage.getItem("cw_site_token") || "";
    axios.get(`${API}/site/config`, { params: { token } })
      .then(({ data }) => setState(data))
      .catch(() => setState({ unlocked: true, content: {} }));
  };
  useEffect(load, []);
  if (!state) return <div className="h-screen" style={{ background: "var(--mf-bg)" }} />;
  return (
    <SiteContext.Provider value={{ content: state.content }}>
      <PageTracker />
      {state.unlocked ? children : <ComingSoon content={state.content} onUnlock={load} />}
    </SiteContext.Provider>
  );
};
